import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "../../../../app/store";
import {
  changeMembershipAction,
  completeFreeMembershipAction,
  selectCheckout,
} from "../../slices/checkoutSlice";
import { getMembershipDetails } from "../../../../api/getMembershipDetails/getMembershipDetailsApi";
import { Membership } from "../../../../api/getMembershipDetails/types";
import Loader from "../../../../components/Loader";

// Right-hand column of the checkout when the total is $0 (Complimentary tier).
// Lets the member either finish for free or upgrade to a paid tier — picking an
// upgrade swaps the session product and the checkout re-renders as a normal
// Stripe card flow (a free member has no saved card, so they must pay here).
const FreeCheckout: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { membership, error } = useSelector(selectCheckout);

  const [upgradeOptions, setUpgradeOptions] = useState<Membership[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const joinSessionUuid = localStorage.getItem("joinSessionUuid");

  useEffect(() => {
    if (!joinSessionUuid) return;
    getMembershipDetails(joinSessionUuid)
      .then((response) => {
        const paidTiers = response.data.memberships.filter(
          (tier) => parseFloat(tier.price) > 0,
        );
        setUpgradeOptions(paidTiers);
      })
      .catch(() => setUpgradeOptions([]));
  }, [joinSessionUuid]);

  const handleCompleteFree = async () => {
    if (!joinSessionUuid) return;
    setSubmitting(true);
    await dispatch(
      completeFreeMembershipAction(joinSessionUuid, () =>
        navigate("/registration/congratulations"),
      ),
    );
    setSubmitting(false);
  };

  const handleUpgrade = (membershipId: number) => {
    if (!joinSessionUuid) return;
    setSubmitting(true);
    // The page switches to the Stripe flow once the total recalculates, so we
    // don't reset submitting — this component unmounts on success.
    dispatch(changeMembershipAction(joinSessionUuid, membershipId));
  };

  if (submitting) {
    return <Loader />;
  }

  return (
    <div className="flex min-h-full flex-col justify-center p-8 sm:p-12">
      <div className="mx-auto w-full max-w-md">
        <h1 className="text-2xl font-bold text-primary">
          Complete your membership
        </h1>
        <p className="mt-2 text-gray-600">
          Your{" "}
          <span className="font-semibold">{membership?.name ?? "free"}</span>{" "}
          membership is free — nothing is due today.
        </p>

        {error && <div className="mt-4 text-red-500">{error}</div>}

        <button
          className="mt-8 block w-full rounded-md bg-primary px-3.5 py-3 text-center font-semibold text-white shadow hover:bg-primary-dark"
          onClick={handleCompleteFree}
          type="button"
        >
          Complete registration — $0 due today
        </button>

        {upgradeOptions.length > 0 && (
          <div className="mt-10">
            <div className="relative flex items-center">
              <div className="flex-grow border-t border-gray-200" />
              <span className="mx-4 flex-shrink text-sm text-gray-500">
                or get more
              </span>
              <div className="flex-grow border-t border-gray-200" />
            </div>

            <div className="mt-6 space-y-3">
              {upgradeOptions.map((tier) => (
                <button
                  className="flex w-full items-center justify-between rounded-md px-3.5 py-3 text-left font-semibold text-primary ring-1 ring-inset ring-primary hover:bg-primary hover:text-white"
                  key={tier.id}
                  onClick={() => handleUpgrade(tier.id)}
                  type="button"
                >
                  <span>Upgrade to {tier.name}</span>
                  <span>
                    $
                    {parseFloat(tier.price)
                      .toFixed(2)
                      .replace(/\.00$/, "")}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FreeCheckout;
